import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { getArtistById } from '@/features/artists/services/artists.api';
import ArtistTopTracks from '../components/ArtistTopTracks';
import ArtistEditDialog from '@/components/ArtistEditDialog';

interface PageProps { theme: string; toggleTheme: () => void }

type Artist = {
  id: string;
  name: string;
  photoUrl: string;
  followers: number;
  listeners: number;
  genres: string[];
  spotifyId?: string;
};

const ArtistProfilePage: React.FC<PageProps> = ({ theme, toggleTheme }) => {
  const { id } = useParams<{ id: string }>();
  const [artist, setArtist] = useState<Artist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openEdit, setOpenEdit] = useState(false);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const data = await getArtistById(id);
        setArtist({
          id: data.id!,
          name: data.name,
          photoUrl: data.photoUrl || '',
          followers: data.followers ?? 0,
          listeners: data.listeners ?? 0,
          genres: data.genres ?? [],
          ...(data.spotifyId ? { spotifyId: data.spotifyId } : {}),
        } as any);
      } catch (e: any) {
        setError('Artista não encontrado');
      }
    })();
  }, [id]);

  return (
    <div className="min-h-screen bg-body">
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <main className="max-w-5xl mx-auto p-3 md:p-4">
        <div className="mb-4 text-sm">
          <Link to="/artists" className="text-green-600 hover:underline">← Voltar para Artistas</Link>
        </div>

        {error && <p className="text-red-500">{error}</p>}
        {!artist && !error && <p className="text-zinc-500">Carregando...</p>}
        {artist && (
          <section className="bg-surface rounded-xl border border-default p-5">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <img
                src={artist.photoUrl || 'https://placehold.co/240x240?text=Artist'}
                alt={artist.name}
                className="w-32 h-32 md:w-40 md:h-40 rounded-lg object-cover border border-default"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h1 className="text-2xl font-extrabold text-zinc-900 dark:text-white">{artist.name}</h1>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      {(artist as any).nationality && <span className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 border border-default text-muted">{(artist as any).nationality}</span>}
                      {(artist as any).careerStage && <span className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 border border-default text-muted">{(artist as any).careerStage}</span>}
                      {(artist as any).mainGenre && <span className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 border border-default text-muted">{(artist as any).mainGenre}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {artist.spotifyId && (
                      <a href={`https://open.spotify.com/artist/${artist.spotifyId}`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md bg-[#1DB954] text-black font-semibold"><i className="ph ph-spotify-logo" /> Spotify</a>
                    )}
                    <button onClick={()=>setOpenEdit(true)} className="px-3 py-2 rounded-md bg-zinc-900 text-white dark:bg-green-500 dark:text-black text-sm font-semibold">Editar</button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="p-3 rounded-md bg-subtle border border-default">
                    <div className="text-muted">Seguidores</div>
                    <div className="text-lg font-bold">{(artist.followers ?? 0).toLocaleString()}</div>
                  </div>
                  <div className="p-3 rounded-md bg-subtle border border-default">
                    <div className="text-muted">Ouvintes</div>
                    <div className="text-lg font-bold">{(artist.listeners ?? 0).toLocaleString()}</div>
                  </div>
                  <div className="p-3 rounded-md bg-subtle border border-default col-span-2 md:col-span-2">
                    <div className="text-muted">Gêneros</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(artist.genres || []).slice(0,8).map((g,i)=> (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-xs border border-default text-muted">{g}</span>
                      ))}
                      {(!artist.genres || artist.genres.length===0) && <span className="text-xs text-muted">-</span>}
                    </div>
                  </div>
                </div>

                {(artist as any).about && (
                  <div className="mt-4 p-3 rounded-md bg-subtle border border-default">
                    <p className="text-sm text-muted">{(artist as any).about}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {artist?.spotifyId && (
          <div className="mt-6">
            <ArtistTopTracks artistId={artist.spotifyId} />
          </div>
        )}

        {artist && openEdit && (
          <ArtistEditDialog
            open={openEdit}
            onClose={()=>setOpenEdit(false)}
            artist={artist as any}
            onSaved={async (a)=>{ setArtist(a as any); }}
          />
        )}
      </main>
    </div>
  );
};

export default ArtistProfilePage;
